// src/components/Footer.tsx
import React from "react";
import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
  Link as MuiLink,
  IconButton,
  Divider,
  ButtonBase,
} from "@mui/material";

import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import FacebookOutlinedIcon from "@mui/icons-material/FacebookOutlined";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import SvgIcon, { SvgIconProps } from "@mui/material/SvgIcon";

export function TikTokIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path
        fill="currentColor"
        d="M19.321 5.562a5.07 5.07 0 0 1-2.987-2.983A5.13 5.13 0 0 1 15.99 1h-3.53v13.48a2.63 2.63 0 1 1-2.63-2.63c.27 0 .53.04.78.11V8.33a6.16 6.16 0 0 0-.78-.05A6.15 6.15 0 1 0 16 14.48V7.77c1.18.85 2.63 1.35 4.2 1.35V5.64c-.3 0-.6-.03-.879-.078Z"
      />
    </SvgIcon>
  );
}

type Branch = {
  title: string;
  address: string;
  phone: string;
  mapSrc: string; // embed URL
};

const BRANCHES: Branch[] = [
  {
    title: "DHA Branch",
    address: "Y-Block 81-Y, DHA Phase 3, Lahore",
    phone: "042-35741151",
    mapSrc:
      "https://maps.google.com/maps?q=Haroon%20Sharif%20Jewellers%20DHA%20Y-block&t=&z=15&ie=UTF8&iwloc=&output=embed",
  },
  {
    title: "Emporium Mall",
    address: "Emporium Mall, 2nd Floor, Lahore",
    phone: "042-32592448",
    mapSrc:
      "https://maps.google.com/maps?q=Haroon%20Sharif%20Jewellers%20Emporium%20Mall&t=&z=15&ie=UTF8&iwloc=&output=embed",
  },
  {
    title: "Suha Bazaar",
    address: "Suha Bazaar 20-D, Rung Mahal",
    phone: "042-37662099",
    mapSrc:
      "https://maps.google.com/maps?q=Haroon%20Sharif%20Jewellers%20Rung%20Mahal&t=&z=15&ie=UTF8&iwloc=&output=embed",
  },
];

const CARE_LINKS = [
  { label: "Contact Us", href: "/contact" },
  { label: "Privacy Policy", href: "/privacy-policy" },
  { label: "Refund Policy", href: "/refund-policy" },
  { label: "Terms of Service", href: "/terms-of-service" },
];

const accent = "white";
const textSoft = "white";

function mapOpenLink(embedSrc: string) {
  return embedSrc.replace(/(&|\?)output=embed/g, "");
}

/** A modern, compact map card (title stays OUTSIDE now) */
const BranchCard: React.FC<{ b: Branch }> = ({ b }) => {
  const openLink = mapOpenLink(b.mapSrc);

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: "0 12px 36px rgba(0,0,0,.10)",
        bgcolor: "#fff",
        minHeight: 260,
        display: "flex",
        "& .map": {
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          border: 0,
          filter: "grayscale(12%)",
          transform: "scale(1.02)",
          transition: "transform .5s ease",
          pointerEvents: "none",
        },
        "&:hover .map": { transform: "scale(1.06)" },
      }}
    >
      <Box component="iframe" className="map" src={b.mapSrc} loading="lazy" />
      {/* Soft fade so text reads well */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to top, rgba(0,0,0,.35), rgba(0,0,0,0) 60%)",
        }}
      />
      {/* Floating info panel only */}
      <Stack
        spacing={1.25}
        sx={{
          position: "relative",
          p: 2,
          width: "100%",
          justifyContent: "flex-end",
          mt: "auto",
        }}
      >
        <Stack
          spacing={1}
          sx={{
            bgcolor: "rgba(255,255,255,.94)",
            borderRadius: 2,
            p: 1.25,
            backdropFilter: "saturate(140%) blur(2px)",
            boxShadow: "0 6px 18px rgba(0,0,0,.08)",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <PlaceOutlinedIcon
              sx={{ fontSize: 20, color: "black", mt: "2px" }}
            />
            <Typography
              sx={{ color: "black", fontSize: ".95rem", lineHeight: 1.5 }}
            >
              {b.address}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <PhoneInTalkOutlinedIcon sx={{ fontSize: 20, color: "black" }} />

            <MuiLink
              href={`tel:${b.phone.replace(/[^+\d]/g, "")}`}
              underline="none"
              sx={{
                color: "black",
                fontSize: ".95rem",
                fontWeight: 600,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {b.phone}
            </MuiLink>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <ButtonBase
              component={MuiLink}
              href={openLink}
              target="_blank"
              rel="noopener"
              sx={{
                px: 1.2,
                py: 0.4,
                borderRadius: 999,
                color: "black",
                fontWeight: 600,
                fontSize: ".9rem",
                textDecoration: "none",
                "&:hover": { backgroundColor: "rgba(175,153,109,.08)" },
              }}
            >
              Directions
            </ButtonBase>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

const Footer: React.FC = () => {
  const year = new Date().getFullYear();

  const scrollTop = () =>
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

  return (
    <Box
      component="footer"
      sx={{
        background: "#000000",
        borderTop: "1px solid #eee",
        pt: { xs: 6, md: 8 },
        pb: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          {/* Top grid: modern branch cards + customer care */}
          <Grid container spacing={{ xs: 4, md: 6 }}>
            {/* Branch columns */}
            {BRANCHES.map((b) => (
              <Grid key={b.title} item xs={12} md={3}>
                {/* Title ABOVE the card now */}
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: `"Bodoni Moda", serif`,
                    fontWeight: 700,
                    letterSpacing: ".02em",
                    color: accent,
                    mb: 1.25,
                    textAlign: "center",
                  }}
                >
                  {b.title}
                </Typography>
                <BranchCard b={b} />
              </Grid>
            ))}

            {/* Customer care column */}
            <Grid item xs={12} md={3}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: `"Bodoni Moda", serif`,
                  fontWeight: 700,
                  letterSpacing: ".02em",
                  color: accent,
                  mb: 1.5,
                }}
              >
                Customer Care
              </Typography>

              <Stack spacing={1.1}>
                {CARE_LINKS.map((l) => (
                  <MuiLink
                    key={l.label}
                    href={l.href}
                    underline="none"
                    sx={{
                      color: textSoft,
                      fontSize: ".98rem",
                      transition: "color .2s ease",
                      "&:hover": { color: accent },
                    }}
                  >
                    {l.label}
                  </MuiLink>
                ))}
              </Stack>
            </Grid>
          </Grid>

          {/* Separator */}
          <Divider
            sx={{ my: { xs: 4, md: 5 }, borderColor: "rgba(0,0,0,0.08)" }}
          />

          {/* Bottom bar: social + copyright + back-to-top */}
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={1.5}>
                {/* Facebook */}
                <IconButton
                  aria-label="Facebook"
                  size="small"
                  component={MuiLink}
                  href="https://web.facebook.com/haroonsharifjewellers/?_rdc=1&_rdr#"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FacebookOutlinedIcon sx={{ color: textSoft }} />
                </IconButton>

                {/* Instagram */}
                <IconButton
                  aria-label="Instagram"
                  size="small"
                  component={MuiLink}
                  href="https://www.instagram.com/haroonsharifjewellers/?hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <InstagramIcon sx={{ color: textSoft }} />
                </IconButton>

                <IconButton
                  aria-label="Tumblr"
                  size="small"
                  component={MuiLink}
                  href="https://www.tiktok.com/@haroonsharifjewellers"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <TikTokIcon sx={{ color: textSoft }} />
                </IconButton>

                {/* YouTube */}
                <IconButton
                  aria-label="YouTube"
                  size="small"
                  component={MuiLink}
                  href="https://www.youtube.com/c/haroonsharifjewellers"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <YouTubeIcon sx={{ color: textSoft }} />
                </IconButton>
              </Stack>
            </Grid>

            <Grid item xs />

            <Grid item xs={12} md="auto">
              <Typography sx={{ color: textSoft, fontSize: ".95rem" }}>
                © {year} Haroon Sharif Jewellers — All rights reserved
              </Typography>
            </Grid>

            <Grid item xs={12} md="auto">
              <IconButton
                aria-label="Back to top"
                onClick={scrollTop}
                sx={{
                  border: "1px solid rgba(0,0,0,0.12)",
                  borderRadius: "999px",
                  width: 36,
                  height: 36,
                  ml: { md: 2 },
                }}
              >
                <ArrowUpwardRoundedIcon
                  sx={{ color: textSoft, fontSize: 20 }}
                />
              </IconButton>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
